import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle, Eye, Link2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePublishSurveyTemplate } from '@/features/surveys/api/use-publish-template';
import { CreateCampaignDialog } from '@/features/surveys/components/CreateCampaignDialog';
import { SurveyTemplatePreviewDialog } from '@/features/surveys/components/SurveyTemplatePreviewDialog';
import { useSurveyTemplate } from '@/features/surveys/api/use-survey-templates';
import { useUpdateSurveyTemplate } from '@/features/surveys/api/use-update-template';
import { TemplateBuilderForm } from '@/features/surveys/components/TemplateBuilderForm';
import type { SurveyTemplateCreateInput } from '@/features/surveys/types';

export const Route = createFileRoute('/_authenticated/surveys/templates/$templateId')({
  component: EditTemplatePage,
});

function EditTemplatePage() {
  const { t } = useTranslation('surveys');
  const { templateId } = Route.useParams();
  const navigate = useNavigate();
  const { data: template, isLoading } = useSurveyTemplate(templateId);
  const updateMutation = useUpdateSurveyTemplate();
  const publishMutation = usePublishSurveyTemplate();
  const isStatusPending = updateMutation.isPending || publishMutation.isPending;
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isCampaignDialogOpen, setIsCampaignDialogOpen] = useState(false);

  const handleSubmit = async (data: SurveyTemplateCreateInput) => {
    try {
      await updateMutation.mutateAsync({ templateId, data });
      toast.success(t('toast.templateSavedSuccessfully'));
    } catch {
      toast.error(t('toast.failedToSaveTemplate'));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">{t('editTemplate.notFound')}</p>
      </div>
    );
  }

  const handlePublish = async () => {
    try {
      await publishMutation.mutateAsync(templateId);
      toast.success(t('toast.templatePublishedSuccessfully'));
    } catch {
      toast.error(t('toast.failedToPublishTemplate'));
    }
  };

  const handleMoveToDraft = async () => {
    try {
      await updateMutation.mutateAsync({
        templateId,
        data: { status: 'draft' },
      });
      toast.success('Template moved to draft');
    } catch {
      toast.error('Failed to move template to draft');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">
              {t('editTemplate.title', { name: template.name })}
            </h1>
            <Badge variant={template.status === 'published' ? 'default' : 'outline'}>
              {template.status}
            </Badge>
          </div>
          <p className="text-muted-foreground">{t('editTemplate.description')}</p>
        </div>
        <div className="flex items-center gap-2">
          {template.status === 'published' && (
            <Button
              variant="outline"
              onClick={() => void handleMoveToDraft()}
              disabled={isStatusPending}
              data-testid="move-template-to-draft-button"
            >
              {updateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Move to draft
            </Button>
          )}
          {template.status !== 'published' && (
            <Button
              onClick={() => void handlePublish()}
              disabled={isStatusPending}
              data-testid="publish-template-button"
            >
              {publishMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle className="mr-2 h-4 w-4" />
              )}
              {t('editTemplate.publish')}
            </Button>
          )}
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Use this template</CardTitle>
          <CardDescription>
            Preview the respondent experience, then create a shareable campaign to collect responses
            from this template.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>Preview shows how the form will look before you publish or share it.</p>
            <p>
              Share link and response collection are handled through survey campaigns created from
              this template.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsPreviewOpen(true);
              }}
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview form
            </Button>
            <Button
              onClick={() => {
                setIsCampaignDialogOpen(true);
              }}
              disabled={template.status !== 'published'}
            >
              <Link2 className="mr-2 h-4 w-4" />
              Share link & collect responses
            </Button>
          </div>
        </CardContent>
      </Card>

      <TemplateBuilderForm
        mode="edit"
        template={template}
        onSubmit={handleSubmit}
        onCancel={() => void navigate({ to: '/surveys' })}
        isPending={isStatusPending}
      />

      <SurveyTemplatePreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        template={template}
      />

      <CreateCampaignDialog
        isOpen={isCampaignDialogOpen}
        onClose={() => {
          setIsCampaignDialogOpen(false);
        }}
        preselectedTemplateId={template.id}
        onSuccess={(campaign) => {
          void navigate({
            to: '/surveys/campaigns/$campaignId',
            params: { campaignId: campaign.id },
          });
        }}
      />
    </div>
  );
}
